import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthController } from '@/features/auth/controller/auth.controller';
import { api } from '@/services/api';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/languages';

// Module-wide, not per component: one call per app start and per language change,
// even if the shell remounts.
let stored: { userId: string; language: SupportedLanguage } | null = null;
// Calls go one after the other: two quick changes (fr → de → fr) must not reach
// the server in the wrong order and leave the account on « de ».
let queue: Promise<void> = Promise.resolve();

/** Test hook: forget what this tab already sent. */
export function resetAccountLanguageSync(): void {
  stored = null;
  queue = Promise.resolve();
}

/** Same marker as `useImpersonationExit`: a super admin browsing as someone else. */
function isImpersonating(): boolean {
  try {
    return !!localStorage.getItem('originalAdminToken');
  } catch {
    return false;
  }
}

/**
 * Sends the app language to the account once logged in and on every change, so the
 * server writes its emails in it (VOL-326, VOL-341). Fire and forget: a failure
 * (offline, back not yet deployed) is retried at the next app start. Never while
 * impersonating, or the admin's language would land on the other account.
 */
export function useAccountLanguageSync(): void {
  const userId = useAuthController((s) => s.user?.id);
  const { i18n } = useTranslation();
  const language = SUPPORTED_LANGUAGES.find((l) => i18n.language?.startsWith(l)) ?? 'fr';

  useEffect(() => {
    if (!userId || isImpersonating()) return;
    if (stored?.userId === userId && stored.language === language) return;
    const sent = { userId, language };
    stored = sent;
    queue = queue.then(() =>
      api.updateLanguage(language).catch(() => {
        if (stored === sent) stored = null;
      }),
    );
  }, [userId, language]);
}
